import React from 'react';
import { AppShell, CookieBanner } from '@/components';
import { MarketingSection, SectionHeader, GlassPanel } from '@/components/marketing';
import { pricingPlans } from '@/content/pricing';
import styles from './Landing.module.css';

const valuePillars = [
  {
    title: 'Portable identity',
    description: 'Track versions, lineage, and environments for every function no matter where it runs. Promote with full history, roll back with confidence.',
  },
  {
    title: 'Operational clarity',
    description: 'Live dashboards expose latency, cost, memory, violations, and alerts. Automations keep your SLAs healthy across clouds.',
  },
  {
    title: 'Built-in governance',
    description: 'Namespace policies, access reviews, and automated approvals ensure only the right tenants touch the right workloads.',
  },
  {
    title: 'Commercial momentum',
    description: 'Price, meter, and bill for reusable logic with entitlement workflows, ledgers, and payouts—no custom tooling required.',
  },
];

const lifecycleSteps = [
  {
    name: 'Publish',
    description: 'Use Relay Studio to scaffold, test, and ship functions with branch-aware RUFIDs and version graph history.',
  },
  {
    name: 'Govern',
    description: 'Apply namespace policies, telemetry budgets, and compliance automations that travel with every deployment.',
  },
  {
    name: 'Monetize',
    description: 'Expose pricing catalogs, entitlements, and usage ledgers so product and finance teams can turn logic into revenue.',
  },
];

const proofBadges = ['SOC2-ready controls', 'GraphQL + REST APIs', 'CLI + Terraform provider'];

const landingPlans = pricingPlans
  .filter((plan) => plan.showOnLanding)
  .sort((a, b) => (a.landingOrder ?? 0) - (b.landingOrder ?? 0));

const LandingPage: React.FC = () => {
  return (
    <AppShell heroWatchId="hero-cta-sentinel">
      <div className={styles.page}>
        <MarketingSection theme="gradient" collapsePadding>
          <div className={styles.heroGrid}>
            <div className={styles.heroCopy}>
              <SectionHeader
                overline="Relay Platform"
                title="Operate every function with confidence, insight, and control."
                description="Relay unifies deployment, runtime policy, observability, and revenue for your serverless estate. Ship to any cloud, govern every execution, and turn reusable logic into products without fragmenting tooling."
              />
              <div className={styles.heroButtons}>
                <a
                  href="/dashboard"
                  className={styles.ctaPrimary}
                >
                  Launch the console
                </a>
                <a
                  href="/docs"
                  className={styles.ctaSecondary}
                >
                  Explore the docs
                </a>
              </div>
              <dl className={styles.heroMetrics}>
                <div>
                  <dt className={styles.metricTitle}>Cloud coverage</dt>
                  <dd className={styles.metricValue}>AWS • Cloudflare • Vercel • self-host</dd>
                </div>
                <div>
                  <dt className={styles.metricTitle}>Latency guardrails</dt>
                  <dd className={styles.metricValue}>P90 &lt; 500 ms by default</dd>
                </div>
                <div>
                  <dt className={styles.metricTitle}>Policy enforcement</dt>
                  <dd className={styles.metricValue}>Namespace isolation &amp; access rules</dd>
                </div>
                <div>
                  <dt className={styles.metricTitle}>Monetization</dt>
                  <dd className={styles.metricValue}>Usage-led ledgers &amp; payouts</dd>
                </div>
              </dl>
            </div>
            <GlassPanel padding="lg" className={`${styles.heroConsole} relative overflow-hidden shadow-2xl shadow-emerald-500/20`}>
              <div className="text-xs font-mono text-emerald-300 uppercase tracking-[0.3em]">Multi-cloud deploy</div>
              <pre className={styles.heroConsoleCode}>
{`$ relay push checkout-handler.ts --targets aws,cloudflare,vercel
✓ packaged function bundle (6.3 KB)
✓ applied network + runtime policy (isolation: strict)
✓ pushed to AWS Lambda  (us-east-1)  latency budget 250ms
✓ pushed to Cloudflare Workers (global) concurrency 1000
✓ pushed to Vercel Functions (iad1)   cold starts monitored

Function ID: func_checkout_handler
Audit Trail: console → executions → checkout-handler
`}
              </pre>
            </GlassPanel>
          </div>
        </MarketingSection>

        <div id="hero-cta-sentinel" className={styles.heroSentinel} aria-hidden="true" />

        <MarketingSection theme="dark">
          <SectionHeader
            overline="Why Relay"
            title="The control plane built for shared serverless logic"
            description="Relay wraps every function with portable identity, policy, observability, and monetization so teams focus on outcomes—not glue code."
            align="center"
          />
          <div className={`${styles.valueGrid} mt-12`}>
            {valuePillars.map((pillar) => (
              <GlassPanel key={pillar.title}>
                <h3 className="text-lg font-semibold text-white">{pillar.title}</h3>
                <p className="mt-3 text-sm text-slate-300">{pillar.description}</p>
              </GlassPanel>
            ))}
          </div>
        </MarketingSection>

        <MarketingSection theme="deep">
          <div className={styles.lifecycleGrid}>
            <div>
              <SectionHeader
                overline="Lifecycle"
                title="One control plane across publish, govern, and monetize"
                description="Relay keeps developers, operators, and revenue teams aligned. Each stage feeds the next with telemetry, policy, and commercial data."
              />
              <div className={`${styles.lifecycleSteps} mt-8`}>
                {lifecycleSteps.map((step) => (
                  <div key={step.name} className={styles.lifecycleStep}>
                    <div className={styles.lifecycleBadge}>{step.name[0]}</div>
                    <div>
                      <h4 className="text-base font-semibold text-white">{step.name}</h4>
                      <p className="mt-1 text-sm leading-6 text-slate-300">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <GlassPanel padding="lg" className="space-y-6">
              <h3 className="text-lg font-semibold text-white">Studio preview</h3>
              <p className="text-sm text-slate-300">
                Relay Studio provides a glassmorphic workspace where developers can draft, test, and iterate on functions with real telemetry and policy context. Every change syncs to the version graph and branch-aware promotion flow.
              </p>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 font-mono text-xs text-slate-200">
                <div className="text-emerald-300">studio.run(&quot;checkout-handler&quot;)</div>
                <div className="mt-2 text-slate-300">✔ Tests passed (2.1s)</div>
                <div className="text-slate-300">✔ Policy checks (latency budget, namespace isolation)</div>
                <div className="text-slate-300">→ Ready for promotion to production</div>
              </div>
            </GlassPanel>
          </div>
        </MarketingSection>

        <MarketingSection theme="dark">
          <SectionHeader
            overline="Proof"
            title="Why leading teams choose Relay"
            description="From multi-cloud latency targets to billing-ready ledgers, Relay keeps operational confidence and business outcomes in lockstep."
          />
          <div className={`${styles.proofGrid} mt-12`}>
            <GlassPanel padding="lg" className="space-y-4">
              <ul className="space-y-4 text-base leading-7 text-slate-300">
                <li><span className="font-semibold text-white">Multi-cloud freedom:</span> Deploy to AWS, Cloudflare, Vercel, or your own metal without refactoring pipelines.</li>
                <li><span className="font-semibold text-white">One observability plane:</span> Metrics, logs, traces, and alerts curated for functions—no stitching dashboards by hand.</li>
                <li><span className="font-semibold text-white">Guardrails by default:</span> Namespaces, policies, secrets, and compliance baked into every rollout.</li>
                <li><span className="font-semibold text-white">Business ready:</span> Pricing catalogs, entitlement workflows, and revenue reporting out of the box.</li>
              </ul>
              <div className={styles.badgeList}>
                {proofBadges.map((badge) => (
                  <span key={badge} className={styles.badge}>
                    {badge}
                  </span>
                ))}
              </div>
            </GlassPanel>
            <GlassPanel padding="lg" className="shadow-lg shadow-black/30">
              <h3 className="text-lg font-semibold text-white">Lifecycle snapshot</h3>
              <ul className="mt-6 space-y-4 text-sm leading-6 text-slate-300">
                <li><span className="text-emerald-400">●</span> Weekly deployments across 3 clouds</li>
                <li><span className="text-emerald-400">●</span> 99.98% availability with automated rollback</li>
                <li><span className="text-emerald-400">●</span> 42% reduction in incident resolution time</li>
                <li><span className="text-emerald-400">●</span> Revenue ledger covers billing, refunds, payouts</li>
              </ul>
            </GlassPanel>
          </div>
        </MarketingSection>

        <MarketingSection id="pricing" theme="deep">
          <SectionHeader
            overline="Plans"
            title="Choose the track that fits your program"
            description="Self-manage Relay on your own infrastructure for free, or scale with our managed console when governance, insights, and commerce matter."
            align="center"
          />
          <div className={`${styles.planGrid} mt-12`}>
            {landingPlans.map((plan) => {
              const highlight = plan.highlight;
              const bulletItems = plan.limits && plan.limits.length > 0 ? plan.limits : plan.capabilities.slice(0, 3);
              return (
                <GlassPanel
                  key={plan.id}
                  padding="lg"
                  className={highlight ? 'border-emerald-500 bg-emerald-500/10 shadow-2xl shadow-emerald-500/20' : undefined}
                >
                  {plan.badge && (
                    <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-300">
                      {plan.badge}
                    </span>
                  )}
                  <div className="mt-3 space-y-2">
                    <h3 className="text-xl font-semibold text-white">{plan.name}</h3>
                    <p className="text-3xl font-bold text-white">{plan.price}</p>
                    {plan.priceNote && <p className="text-xs uppercase tracking-wide text-slate-400">{plan.priceNote}</p>}
                    <p className="text-sm text-slate-300">{plan.tagline}</p>
                  </div>
                  <ul className={`${styles.planList} mt-5 text-sm leading-6`}>
                    {bulletItems.map((item) => (
                      <li key={item}>• {item}</li>
                    ))}
                  </ul>
                  <a
                    href={plan.cta.href}
                    className={`mt-auto inline-flex w-full items-center justify-center rounded-lg px-6 py-3 text-sm font-semibold transition ${
                      highlight
                        ? 'bg-emerald-500 text-slate-950 hover:bg-emerald-400'
                        : 'border border-white/20 text-white hover:border-white'
                    }`}
                  >
                    {plan.cta.label}
                  </a>
                </GlassPanel>
              );
            })}
          </div>
        </MarketingSection>

        <MarketingSection theme="dark">
          <SectionHeader
            overline="Get started"
            title="Ready to operate every function with confidence?"
            description="Launch the console to publish your next function, or talk with us about how Relay fits your platform roadmap."
            align="center"
            actions={[
              <a
                key="console"
                href="/dashboard"
                className={styles.ctaPrimary}
              >
                Get started
              </a>,
              <a
                key="contact"
                href="/contact"
                className={styles.ctaSecondary}
              >
                Book a strategy session
              </a>,
            ]}
          />
        </MarketingSection>

        <CookieBanner />
      </div>
    </AppShell>
  );
};

export default LandingPage;
